// What an agent did inside a running system.
//
// Not "kubectl calls" — the reading is: which deployed service did a session reach
// into, what did it want to know, and did it find out.
//
//   kubectl -n prod-backend logs --since=60s --tail=30 skwach-6db6ddc76d-d2wzp -c skwach
//   POD=$(kubectl --context "$PROD_CTX" -n prod-backend get po -l app=conversation-normalizer -o jsonpath='{.items[0].metadata.name}')
//   kubectl --context "$PROD_CTX" -n prod-backend exec "$POD" -c conversation-normalizer -- python -c "...confluent_kafka..."
//
// THE POD NAME IS NOT THE TARGET, and this is what makes the lens possible.
// `skwach-6db6ddc76d-d2wzp`, `skwach-6db6ddc76d-795j7` and `skwach-6ff7777686-45dxn`
// are three pods of ONE service, and the suffix is regenerated on every deploy.
// Keyed on the pod this reports one-off touches of names nobody recognises; keyed
// on the service it reports repeated inspection of `prod-backend/conversation-normalizer` - a thing
// a reader knows, that outlives every session touching it. The pod is often a
// variable resolved by an earlier statement, so the name is also recovered from a
// label selector when it is not literal.
//
// The split that carries meaning is PROBE versus LOGS: one runs code inside the
// service, the other reads what it already emitted. And what a probe asked is the
// API surface of the program it ran — `confluent_kafka` with a Consumer reading
// offsets is a lag check, whatever the surrounding shell looks like.

import {
  HARNESS_NOTICE_RE,
  SHELL_ERROR_RE,
  SHELL_TOOL_NAMES,
  resolveShellVars,
  splitStatements,
  stripAnsi,
  tokenize,
} from "../../src/mine/shell.js";
import { embeddedSource, skeletonizeScript } from "../../src/mine/skeleton.js";
import { readServiceState } from "./state.js";

const OUT_CAP = 4000;
const str = (v) => (typeof v === "string" ? v : "");
const contentText = (c) => (typeof c === "string" ? c
  : Array.isArray(c) ? c.map((p) => (typeof p === "string" ? p : str(p?.text))).join("\n") : str(c?.text));

const KUBE_RE = /^(?:kubectl|oc|k)$/i;
const RESOURCE_TYPE_RE = /^(?:po|pod|pods|deploy|deployment|deployments|sts|statefulsets?|svc|services?|job|jobs|cronjobs?|ds|daemonsets?|rs|replicasets?|pvc|pv|ns|namespaces?|nodes?|events?|secrets?|cm|configmaps?|ing|ingress(?:es)?|hpa|crd|all)$/i;

// Generated suffixes: a Deployment's pod is `<name>-<replicaset>-<pod>`, a
// StatefulSet's is `<name>-<ordinal>`.
const REPLICASET_POD_RE = /-[0-9a-z]{6,10}-[0-9a-z]{5}$/i;
const STATEFULSET_POD_RE = /-\d{1,3}$/;

// Shell punctuation survives tokenizing. `2>&1` was read as the thing being
// addressed and became the single largest "service" in the corpus at 71 ops; a
// line-continuation backslash did the same on a smaller scale.
const PUNCTUATION_RE = /^(?:\d?[<>]{1,2}&?\d?|[|&;()]{1,2}|\\|\/dev\/null)$/;

/** The stable service name behind a generated pod name, and behind `deploy/x`. */
export function serviceOf(name) {
  const n = String(name || "").trim().replace(/^[a-z]+\//i, (m) => (RESOURCE_TYPE_RE.test(m.slice(0, -1)) ? "" : m));
  if (!n || n.startsWith("$") || n.includes("{") || PUNCTUATION_RE.test(n)) return null;
  if (REPLICASET_POD_RE.test(n)) return n.replace(REPLICASET_POD_RE, "");
  if (STATEFULSET_POD_RE.test(n)) return n.replace(STATEFULSET_POD_RE, "");
  return n;
}

/** `-l app=conversation-normalizer` -> the service it names, for when the pod is a variable. */
export function serviceFromSelector(text) {
  const m = /(?:-l|--selector)[= ]\s*["']?([^"'\s]+)/.exec(String(text || ""));
  if (!m) return null;
  const pair = m[1].split(",").map((p) => p.split("=")).find(([k]) => /(?:^|\/)(?:app|name|component)$/i.test(k));
  return pair?.[1] || null;
}

// Flags consuming the next token. Without this the first "bare" token after
// `kubectl -n prod-backend` is the NAMESPACE, and reading it as the verb silently
// dropped eleven of twelve real calls.
const VALUE_FLAGS = new Set(["-n", "--namespace", "-c", "--container", "--context", "-l", "--selector",
  "-o", "--output", "--tail", "--since", "--since-time", "--kubeconfig", "--request-timeout",
  "--timeout", "--replicas", "--type", "--patch", "-f", "--filename", "--user", "--cluster", "--field-selector"]);

/** kubectl's own arguments: everything before the `--` that hands off to a shell. */
export const beforeSeparator = (argv) => {
  const at = argv.indexOf("--");
  return at === -1 ? argv : argv.slice(0, at);
};

/** Genuinely positional tokens, with flag values and punctuation removed. */
export function positionals(argv) {
  const out = [];
  for (const [i, t] of beforeSeparator(argv).entries()) {
    if (t.startsWith("-")) continue;
    if (PUNCTUATION_RE.test(t)) continue;
    const prev = argv[i - 1];
    if (prev && VALUE_FLAGS.has(prev)) continue;
    out.push(t);
  }
  return out;
}

const flagValue = (argv, ...names) => {
  const own = beforeSeparator(argv);
  for (let i = 0; i < own.length; i += 1) {
    const t = own[i];
    for (const n of names) {
      if (t === n) return own[i + 1] || null;
      if (t.startsWith(`${n}=`)) return t.slice(n.length + 1) || null;
    }
  }
  return null;
};

/** What the agent went in to do. `exec` runs code inside; `logs` reads what leaked out. */
export function kindOf(verb) {
  if (/^exec$/i.test(verb)) return "probe";
  if (/^(?:logs|log)$/i.test(verb)) return "logs";
  if (/^(?:delete|scale|apply|patch|rollout|cordon|drain|edit|annotate|label|create|replace)$/i.test(verb)) return "change";
  if (/^(?:get|describe|top|explain)$/i.test(verb)) return "inspect";
  return "other";
}

/** The window a log read asked for — the question "what happened in the last N". */
export function windowOf(argv) {
  const since = flagValue(argv, "--since");
  const tail = flagValue(argv, "--tail");
  return [since ? `last ${since}` : null, tail ? `${tail} lines` : null].filter(Boolean).join(" · ") || null;
}

/** What a probe asked, as the API surface of the program it ran inside the container. */
export function probeSubject(text) {
  const src = embeddedSource(text);
  if (!src) return null;
  const skel = skeletonizeScript(src);
  const body = skel ? skel.replace(/^script:\s*/, "").trim() : "";
  return body || null;
}

const ENV_RE = /\b(prod|production|staging|stage|devint|preprod|sandbox|dev)\b/i;
function envOf(s) {
  const m = ENV_RE.exec(String(s || ""));
  return m ? m[1].toLowerCase().replace("production", "prod").replace(/^stage$/, "staging") : null;
}

const REFUSED_RE = /command terminated with exit code|\bError from server\b|\bUnable to connect\b|\bforbidden\b|\bNotFound\b|\bUnauthorized\b/i;
const RAISED_RE = /Traceback \(most recent call last\)|\b(?:KeyError|ModuleNotFoundError|ConnectionError|TimeoutError|ImportError)\b/;

export function extractLiveOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const t of turns) {
    for (const b of t?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }

  for (const t of turns) {
    for (const b of t?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      if (!SHELL_TOOL_NAMES.test(String(b.name || "").split("__").pop())) continue;
      const cmd = str(b.input?.command) || str(b.input?.cmd) || str(b.input?.script);
      if (!cmd) continue;
      const text = resolveShellVars(stripAnsi(cmd));

      const result = b.id ? resultById.get(b.id) : null;
      const errored = Boolean(result?.isError) || (result ? contentText(result.content).includes("<tool_use_error>") : false);
      const rawOut = !errored && result ? contentText(result.content) : "";
      const outputStatus = !rawOut ? "none"
        : HARNESS_NOTICE_RE.test(rawOut) ? "backgrounded"
        : SHELL_ERROR_RE.test(rawOut) ? "shell-error"
        : "observed";
      const out = outputStatus === "observed" ? stripAnsi(rawOut) : "";

      // One call often resolves the pod in one statement and enters it in the
      // next, so an earlier selector names what the later `exec "$POD"` cannot.
      const statements = splitStatements(text);
      let selectorService = null;
      for (const st of statements) if (!selectorService) selectorService = serviceFromSelector(st.text);

      statements.forEach((st, n) => {
        const argv = tokenize(st.text.trim()).filter(Boolean);
        let i = 0;
        while (i < argv.length && (/^\$?\w+=/.test(argv[i]) || /^(?:sudo|env|time|timeout|exec)$/i.test(argv[i]))) {
          i += /^timeout$/i.test(argv[i]) ? 2 : 1;
        }
        if (!KUBE_RE.test((argv[i] || "").split(/[\\/]/).pop())) return;

        const rest = argv.slice(i + 1);
        const bare = positionals(rest);
        const verb = bare[0] || "";
        const kind = kindOf(verb);
        if (kind === "other") return;

        const namespace = flagValue(rest, "-n", "--namespace");
        const context = flagValue(rest, "--context");
        // The namespace carries the environment as often as the context does —
        // half these calls name no context and rely on the current one.
        const environment = envOf(context) || envOf(namespace);

        // `rollout restart deploy/x` — `restart` is a subcommand, not a name.
        const SUBCOMMANDS = /^(?:restart|status|undo|history|pause|resume|top|node|nodes)$/i;
        const named = bare.slice(1).find((a) => !RESOURCE_TYPE_RE.test(a) && !SUBCOMMANDS.test(a));
        const service = serviceOf(named) || selectorService;
        // A namespace-wide list names no single service, and that is not a gap:
        // the thing being looked at IS the namespace. Falling through to an
        // "unidentified" bucket shattered 74 ops into singletons that all meant
        // "surveyed this namespace".
        const scope = service ? "service" : (namespace ? "namespace" : "cluster");

        // THE FINDING, ON THE OP. A row reading "inspect forgejo-runner/scaledobject ·
        // answered" says an inspection happened and returned something; it does not
        // say what was found, which is the only reason the lens exists. The state was
        // being computed at GROUP level for the panel, so the trace feed — which
        // renders one op at a time — had nothing but an outcome word to show.
        const observed = readServiceState(
          { output: out, kind, service: service || null },
          serviceOf,
        );
        const found = observed
          ? [observed.ready ? `${observed.ready} ready` : null,
             observed.status,
             // `0 restarts` is a fact about a healthy pod, not an absence.
             // readServiceState already distinguishes the two via restartsSeen.
             observed.restarts != null ? `${observed.restarts} restarts` : null].filter(Boolean).join(" · ")
          : null;

        ops.push({
          key: statements.length > 1 ? `${b.id}#${n}` : (b.id || `synth:live:${service || namespace}`),
          found,
          kind,
          verb: verb.toLowerCase(),
          scope,
          service: service || null,
          resource: named && RESOURCE_TYPE_RE.test(bare[1] || "") ? bare[1].toLowerCase() : null,
          // Kept as evidence, never as identity.
          pod: named && named !== service ? named : null,
          namespace: namespace || null,
          context: context || null,
          environment,
          container: flagValue(rest, "-c", "--container"),
          window: kind === "logs" ? windowOf(rest) : null,
          probe: kind === "probe" ? probeSubject(st.text) : null,
          // Display projections the generic panel reads.
          steps: [
            { kind: verb.toLowerCase(), cmd: st.text.trim().replace(/\s+/g, " ").slice(0, 300) },
            kind === "probe" && probeSubject(st.text) ? { kind: "asked", cmd: probeSubject(st.text).slice(0, 240) } : null,
          ].filter(Boolean),
          checks: [{
            kind: kind === "probe" ? "ran inside" : kind,
            tool: [environment, namespace, service].filter(Boolean).join("/") || "unscoped",
            // The finding leads: it is what a reader wants off a single row.
            scope: [found, flagValue(rest, "-c", "--container"), kind === "logs" ? windowOf(rest) : null].filter(Boolean),
            outcome: !out ? "unrecorded" : RAISED_RE.test(out) ? "raised" : REFUSED_RE.test(out) ? "refused" : "answered",
          }],
          outcome: !out ? "unrecorded"
            : RAISED_RE.test(out) ? "raised"
            : REFUSED_RE.test(out) ? "refused"
            : "answered",
          output: out ? (out.length > OUT_CAP ? out.slice(0, OUT_CAP) : out) : null,
          outputStatus,
          ok: !errored,
        });
      });
    }
  }
  return ops;
}

/** Why this op shows no answer. Only some of these are the cluster's doing. */
export function describeMissing(op) {
  // Absence of evidence outranks absence of a result.
  switch (op.outputStatus) {
    case "backgrounded": return "ran in the background — what the system returned never entered this trace";
    case "shell-error": return "the captured text was the shell's error, not the system's response";
    case "none": return "ran and captured no output";
    default: break;
  }
  if (op.outcome === "raised") return "the probe ran inside the service and raised — the agent got in but not to its answer";
  if (op.outcome === "refused") return "the system refused the call — the agent never reached the service";
  return null;
}
